import {useQuery, useQueryClient} from "@tanstack/react-query";
import {Button, Select, Tabs, TextInput} from "@mantine/core";
import { CurrencyDollar as IconCash, ArrowClockwise as IconRefresh, MagnifyingGlass as IconSearch } from "@phosphor-icons/react";
import {DataTable} from "mantine-datatable";
import {useCallback, useContext, useMemo, useState} from "react";
import {Col, Row} from "../../components/Flex/Flex";
import LoaderScreen from "../../components/Loader/LoaderScreen";
import Page from "../../components/Page/Page";
import MessageContext from "../../context/MessageContext";
import api from "../../utils/api";
import CGT from "../../utils/CGT";
import {bodyNumber} from "../../utils/formatters";
import {bodyLicenseId, bodyStatus, typeNames} from "../subscription/shared";
import {usePaySubscribe} from "../subscription/usePaySubscribe";


const getAllSubs = async () => {
    const {data} = await api.get('/admin/subscriptions');
    return data;
}

const bodyTimestamp = (row, config) => {
    if (row[config.field] > 0) {
        const cgt = CGT.fromSeconds(row[config.field]);
        return <span title={cgt.toLocaleString(CGT.DATETIME_SHORT)}>{cgt.toCGT()}</span>;
    }
}
const bodyUser = (row, config) => {
    return row[config.field]?.name;
}

const bodyBuyer = (row) => {
    return <div>{row.buyerName} <small>({row.buyer})</small></div>
}

const PAYMENTS_PAGE_SIZE = 50;
const LICENSES_PAGE_SIZE = 10;

export default function Subscriptions() {
    const {success, error} = useContext(MessageContext);
    const queryClient = useQueryClient();
    const {data, isPending} = useQuery({
        queryKey: ['admin-subscriptions'],
        queryFn: getAllSubs,
    });
    const {verifying, verifyTransaction} = usePaySubscribe({
        onSuccess: (message) => {
            success(message);
            queryClient.invalidateQueries(['admin-subscriptions']);
        },
        onError: (title, message) => {
            error(title, message);
        }
    })

    const bodyActions = useCallback((row) => {
        if (!row.payDate) {
            return (
                <div className="flex flex-row gap-2">
                    <Button onClick={() => verifyTransaction(row.id)} leftSection={<IconRefresh size={16}/>} size="compact-sm" variant="default"/>
                    <Button onClick={() => {
                        if (confirm('Mark this as paid?')) {
                            api.get('/admin/subscriptions/pay/' + row.id).then(() => {
                                success('Transaction marked as paid');
                                queryClient.invalidateQueries({queryKey: ['admin-subscriptions']});
                            })
                        }
                    }} leftSection={<IconCash size={16}/>} size="compact-sm" variant="default">Mark Paid</Button>
                </div>
            )

        }
    }, []);

    const bodyLicenseType = useCallback((row) => {
        if (data) {
            const licenses = data.licenses.filter(el => row.licenses.includes(el.id));
            if (licenses.length === 0) {
                return <div>ERROR</div>
            }
            const type = licenses[0].type;
            return typeNames[type];
        }
    }, [data])

    // Helpers
    const getTransactionType = useCallback((row) => {
        if (!data) {
            return undefined;
        }
        const licenses = data.licenses.filter(el => row.licenses?.includes(el.id));
        if (licenses.length === 0) {
            return undefined;
        }
        return licenses[0].type;
    }, [data]);

    const bodyPayDateGreen = useCallback((row, config) => {
        if (row[config.field] > 0) {
            const cgt = CGT.fromSeconds(row[config.field]);
            return <span style={{color: 'green'}} title={cgt.toLocaleString(CGT.DATETIME_SHORT)}>{cgt.toCGT()}</span>;
        }
    }, []);

    // Filters & search state
    const [paymentsSearch, setPaymentsSearch] = useState('');
    const [paymentsType, setPaymentsType] = useState(null);
    const [paymentsPaid, setPaymentsPaid] = useState(null); // true = paid, false = unpaid

    const [licensesSearch, setLicensesSearch] = useState('');
    const [licensesType, setLicensesType] = useState(null);
    const [licensesStatus, setLicensesStatus] = useState(null);

    // Pagination state
    const [paymentsPage, setPaymentsPage] = useState(1);
    const [licensesPage, setLicensesPage] = useState(1);

    // Sort state
    const [paymentsSortStatus, setPaymentsSortStatus] = useState({columnAccessor: 'created', direction: 'desc'});
    const [licensesSortStatus, setLicensesSortStatus] = useState({columnAccessor: 'created', direction: 'desc'});

    const typeOptions = useMemo(() => Object.entries(typeNames).map(([value, label]) => ({label, value: String(value)})), []);

    const licenseStatusOptions = useMemo(() => {
        if (!data?.licenses) {
            return [];
        }
        const set = new Set(data.licenses.map(l => l.status).filter(Boolean));
        return Array.from(set).map(s => ({label: s, value: s}));
    }, [data]);

    const paidStatusOptions = useMemo(() => [
        {label: 'Paid', value: 'true'},
        {label: 'Unpaid', value: 'false'},
    ], []);

    const filteredTransactions = useMemo(() => {
        if (!data?.transactions) {
            return [];
        }
        const q = paymentsSearch.trim().toLowerCase();
        return data.transactions.filter(t => {
            // search
            const matchesSearch = q.length === 0 || [
                String(t.id ?? ''),
                String(t.buyerName ?? ''),
                String(t.buyer ?? ''),
            ].some(v => v.toLowerCase().includes(q));
            if (!matchesSearch) {
                return false;
            }
            // type filter
            if (paymentsType != null) {
                const ttype = getTransactionType(t);
                const compareValue = isNaN(Number(paymentsType)) ? paymentsType : Number(paymentsType);
                if (ttype !== compareValue) {
                    return false;
                }
            }
            // paid/unpaid
            if (paymentsPaid != null) {
                const isPaid = !!t.payDate;
                if (isPaid !== (paymentsPaid === 'true')) {
                    return false;
                }
            }
            return true;
        });
    }, [data, paymentsSearch, paymentsType, paymentsPaid, getTransactionType]);

    const sortedTransactions = useMemo(() => {
        const sorted = [...filteredTransactions].sort((a, b) => {
            const aVal = a[paymentsSortStatus.columnAccessor];
            const bVal = b[paymentsSortStatus.columnAccessor];
            if (typeof aVal === 'string') return aVal.localeCompare(bVal);
            return (aVal ?? 0) - (bVal ?? 0);
        });
        return paymentsSortStatus.direction === 'desc' ? sorted.reverse() : sorted;
    }, [filteredTransactions, paymentsSortStatus]);

    const paginatedTransactions = useMemo(() => {
        return sortedTransactions.slice((paymentsPage - 1) * PAYMENTS_PAGE_SIZE, paymentsPage * PAYMENTS_PAGE_SIZE);
    }, [sortedTransactions, paymentsPage]);

    const filteredLicenses = useMemo(() => {
        if (!data?.licenses) {
            return [];
        }
        const q = licensesSearch.trim().toLowerCase();
        return data.licenses.filter(l => {
            const matchesSearch = q.length === 0 || [
                String(l.id ?? ''),
                String(l.type ?? ''),
                String(l?.buyer?.name ?? ''),
                String(l?.actualBuyer?.name ?? ''),
                String(l?.targetUser?.name ?? ''),
                String(l.status ?? ''),
            ].some(v => v.toLowerCase().includes(q));
            if (!matchesSearch) {
                return false;
            }
            const compareType = licensesType != null ? (isNaN(Number(licensesType)) ? licensesType : Number(licensesType)) : null;
            if (compareType != null && l.type !== compareType) {
                return false;
            }
            if (licensesStatus != null && l.status !== licensesStatus) {
                return false;
            }
            return true;
        });
    }, [data, licensesSearch, licensesType, licensesStatus]);

    const sortedLicenses = useMemo(() => {
        const sorted = [...filteredLicenses].sort((a, b) => {
            const key = licensesSortStatus.columnAccessor;
            const aVal = key.includes('.') ? key.split('.').reduce((o, k) => o?.[k], a) : a[key];
            const bVal = key.includes('.') ? key.split('.').reduce((o, k) => o?.[k], b) : b[key];
            if (typeof aVal === 'string') return aVal.localeCompare(bVal);
            return (aVal ?? 0) - (bVal ?? 0);
        });
        return licensesSortStatus.direction === 'desc' ? sorted.reverse() : sorted;
    }, [filteredLicenses, licensesSortStatus]);

    const paginatedLicenses = useMemo(() => {
        return sortedLicenses.slice((licensesPage - 1) * LICENSES_PAGE_SIZE, licensesPage * LICENSES_PAGE_SIZE);
    }, [sortedLicenses, licensesPage]);

    if (isPending) {
        return null;
    }

    return (
        <Page>
            <title>Admin - Subscriptions</title>
            {verifying && <LoaderScreen message="Verifying payment"/>}
            <h1>Admin Subscriptions</h1>
            <Tabs defaultValue="payments">
                <Tabs.List>
                    <Tabs.Tab value="payments">Payments</Tabs.Tab>
                    <Tabs.Tab value="licenses">Licenses</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value="payments">
                    <Row mb={2}>
                        <Col>
                            <TextInput
                                leftSection={<IconSearch size={16}/>}
                                placeholder="Search payments"
                                value={paymentsSearch}
                                onChange={e => { setPaymentsSearch(e.target.value); setPaymentsPage(1); }}
                            />
                        </Col>
                        <Col>
                            <Select placeholder="Filter by type" value={paymentsType} onChange={v => { setPaymentsType(v); setPaymentsPage(1); }} data={typeOptions} clearable/>
                        </Col>
                        <Col>
                            <Select placeholder="Paid status" value={paymentsPaid} onChange={v => { setPaymentsPaid(v); setPaymentsPage(1); }}
                                      data={paidStatusOptions} clearable/>
                        </Col>
                    </Row>
                    <DataTable
                        fetching={isPending}
                        records={paginatedTransactions}
                        totalRecords={sortedTransactions.length}
                        recordsPerPage={PAYMENTS_PAGE_SIZE}
                        page={paymentsPage}
                        onPageChange={setPaymentsPage}
                        sortStatus={paymentsSortStatus}
                        onSortStatusChange={setPaymentsSortStatus}
                        columns={[
                            {accessor: 'id', title: 'ID', render: (row) => bodyLicenseId(row, {field: 'id'}), sortable: true},
                            {accessor: 'buyerName', title: 'Buyer', render: (row) => bodyBuyer(row), sortable: true},
                            {accessor: 'created', title: 'Created', render: (row) => bodyTimestamp(row, {field: 'created'}), sortable: true},
                            {accessor: 'payDate', title: 'Pay Date', render: (row) => bodyPayDateGreen(row, {field: 'payDate'}), sortable: true},
                            {accessor: 'price', title: 'Price', render: (row) => bodyNumber(row, {field: 'price'}), sortable: true},
                            {accessor: 'licenseType', title: 'License Type', render: (row) => bodyLicenseType(row)},
                            {accessor: 'actions', title: 'Actions', render: (row) => bodyActions(row)},
                        ]}
                    />
                </Tabs.Panel>
                <Tabs.Panel value="licenses">
                    <Row mb={2}>
                        <Col>
                            <TextInput
                                leftSection={<IconSearch size={16}/>}
                                placeholder="Search licenses"
                                value={licensesSearch}
                                onChange={e => { setLicensesSearch(e.target.value); setLicensesPage(1); }}
                            />
                        </Col>
                        <Col>
                            <Select placeholder="Filter by type" value={licensesType} onChange={v => { setLicensesType(v); setLicensesPage(1); }} data={typeOptions} clearable/>
                        </Col>
                        <Col>
                            <Select placeholder="Filter by status" value={licensesStatus} onChange={v => { setLicensesStatus(v); setLicensesPage(1); }}
                                  data={licenseStatusOptions} clearable />
                        </Col>
                    </Row>
                    <DataTable
                        fetching={isPending}
                        records={paginatedLicenses}
                        totalRecords={sortedLicenses.length}
                        recordsPerPage={LICENSES_PAGE_SIZE}
                        page={licensesPage}
                        onPageChange={setLicensesPage}
                        sortStatus={licensesSortStatus}
                        onSortStatusChange={setLicensesSortStatus}
                        columns={[
                            {accessor: 'id', title: 'ID', render: (row) => bodyLicenseId(row, {field: 'id'}), sortable: true},
                            {accessor: 'type', title: 'Type', sortable: true},
                            {accessor: 'buyer.name', title: 'Buyer', sortable: true},
                            {accessor: 'actualBuyer.name', title: 'Paid By', sortable: true},
                            {accessor: 'created', title: 'Created', render: (row) => bodyTimestamp(row, {field: 'created'}), sortable: true},
                            {accessor: 'payDate', title: 'Pay Date', render: (row) => bodyTimestamp(row, {field: 'payDate'}), sortable: true},
                            {accessor: 'targetUser', title: 'For', render: (row) => bodyUser(row, {field: 'targetUser'})},
                            {accessor: 'expireDate', title: 'Valid until', render: (row) => bodyTimestamp(row, {field: 'expireDate'}), sortable: true},
                            {accessor: 'status', title: 'Status', render: (row) => bodyStatus(row), sortable: true},
                        ]}
                    />
                </Tabs.Panel>
            </Tabs>

        </Page>
    )
}
